import React from "react";
import { Divider, Grid, Stack, Typography } from "@financeable-com-au/financeable-ui";
import { ASSET_DETAILS_PANEL } from "@/constants";
import { AssetDetailsLayoutProps } from "@/utils/interface";
import { BlueflagPPSR } from "@/store/slices/types/applicationFormSlice";
import PPSRDetails from "./PPSRDetails";

/**
 * One label/value line. Every row in either provider's panel is one of these. The pair is grouped and
 * named by its label so it is announced together, rather than as a loose label and a loose figure.
 */
export const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Grid
    container
    xs={12}
    role="group"
    aria-label={label}
    sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
  >
    <Grid xs={12} sm={6}>
      <Typography level="body-sm" fontWeight="bold">
        {label}
      </Typography>
    </Grid>
    <Grid xs={12} sm={6}>
      <Typography level="body-sm" textAlign="left">
        {value || "-"}
      </Typography>
    </Grid>
  </Grid>
);

/** NEVDIS answers for a plate or VIN, and only when it recognised one. */
export const isBlueflagFound = (blueflagPPSR?: BlueflagPPSR) =>
  Boolean(blueflagPPSR?.nevdisData && blueflagPPSR?.nevdisData?.verification_status === "Found");

const BlueflagSection = ({ blueflagPPSRData }: { blueflagPPSRData: BlueflagPPSR }) => {
  const vehicle = blueflagPPSRData?.nevdisData?.nevdis_vehicles?.[0];

  if (!isBlueflagFound(blueflagPPSRData)) return null;

  return (
    <Grid container xs={12} alignContent="flex-start">
      <InfoRow label="VIN:" value={vehicle?.vehicle_identifier?.identifier_value} />
      <InfoRow label="Registration No:" value={vehicle?.vehicle_detail?.registration?.plate} />
      <InfoRow label="Engine No:" value={vehicle?.vehicle_detail?.vehicle_description?.engine_number} />
      <InfoRow label="Compliance Plate:" value={vehicle?.vehicle_detail?.vehicle_description?.compliance_plate} />
      <InfoRow label="Stolen:" value={vehicle?.stolen?.length > 0 ? "Yes" : "No"} />
      <InfoRow label="Written Off:" value={vehicle?.written_off_details?.length > 0 ? "Yes" : "No"} />
      <InfoRow label="Safety Recalls:" value={vehicle?.safety_recalls?.length > 0 ? "Yes" : "No"} />
    </Grid>
  );
};

/**
 * The frame both asset-details panels share. Only the specification column differs by provider —
 * Glass's and AutoGrab return different shapes — so it arrives already rendered, and is passed as
 * `null` when the asset has no specification at all. Everything else on the panel comes from the
 * blueflag lookup, which is the same call whichever provider identified the vehicle.
 */
const AssetDetailsLayout = ({ asset, heading, specifications }: AssetDetailsLayoutProps) => {
  const blueflagPPSRData = asset?.blueflagPPSR;
  const isPPSRData = isBlueflagFound(blueflagPPSRData);

  return (
    <React.Fragment>
      {specifications && (
        <Typography level="body-md" fontWeight={600} style={{ margin: "0 0 10px" }}>
          {heading}
        </Typography>
      )}
      <Stack>
        {/* Columns keep their own height; stretching the shorter one pulls its rows apart. */}
        <Grid container direction="row" alignItems="flex-start" spacing={1}>
          {specifications && (
            <Grid container direction="column" xs={12} sm={12} md={6}>
              {specifications}
            </Grid>
          )}
          {isPPSRData && (
            <Grid container direction="column" xs={12} sm={12} md={6}>
              <BlueflagSection blueflagPPSRData={blueflagPPSRData} />
            </Grid>
          )}
        </Grid>
      </Stack>

      {isPPSRData && (
        <Stack gap={2} style={{ padding: "10px 0 0 0" }}>
          <Divider />
          <Typography level="body-md" fontWeight={600}>
            {ASSET_DETAILS_PANEL.PPSR_HEADING}
          </Typography>
          <PPSRDetails asset={asset} />
        </Stack>
      )}
    </React.Fragment>
  );
};

export default AssetDetailsLayout;
